import { toast } from "react-toastify"

import { api, type RouterOutputs } from "~/utils/api"
import { showErrorToast } from "~/utils/toaster"
import { invalidateSupplyPlanListReads } from "./useSupplyPlan"
import { trackEvent } from "./useTrackingService"

export type IPurchasePlan = RouterOutputs["purchasePlan"]["get"]

export const useCreatePurchasePlan = () => {
  const utils = api.useUtils()

  return api.purchasePlan.create.useMutation({
    onSuccess: async () => {
      await invalidateSupplyPlanListReads(utils)
      utils.purchasePlan.getQtyPlannedDataForPlans.invalidate()
      utils.purchasePlan.getVariantQuantitiesForPlans.invalidate()
      // A new buy takes (collection, warehouse) pairs off the table for every other buy.
      utils.purchasePlan.warehouseClaims.invalidate()
      toast.success("Supply plan created.")
      trackEvent("Supply Plan Created")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useUpdatePurchasePlan = () => {
  const utils = api.useUtils()

  return api.purchasePlan.update.useMutation({
    onSuccess: () => {
      utils.purchasePlan.get.invalidate()
      void invalidateSupplyPlanListReads(utils)
      utils.purchasePlan.getQtyPlannedDataForPlans.invalidate()
      utils.purchasePlan.getVariantQuantitiesForPlans.invalidate()
      // Editing the collections a buy covers moves which pairs it claims.
      utils.purchasePlan.warehouseClaims.invalidate()
      toast.success("Supply plan updated.")
      trackEvent("Supply Plan Updated")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

/**
 * Scope and risk posture of a buy (PRO-1126).
 *
 * Writes the fresh plan into BOTH plan caches rather than invalidating: `get` backs the buy grid's
 * per-warehouse columns, and `getAll` backs the buys list and the collection overview's "is there
 * still a warehouse free to buy?" rule. A refetch would leave the buyer looking at the old numbers
 * while it lands, and re-create every object hanging off the plan.
 *
 * `warehouseClaims` is invalidated instead — it is derived server-side from every active buy, so
 * there is nothing local to patch, and freeing a warehouse here has to unlock it everywhere else.
 *
 * The caller is responsible for confirming the recompute first: this changes every warehouse
 * column and the order that gets placed.
 */
export const useUpdateBuySettings = () => {
  const utils = api.useUtils()

  return api.purchasePlan.updateBuySettings.useMutation({
    onSuccess: (plan) => {
      utils.purchasePlan.get.setData({ id: plan.id }, (previous) =>
        previous ? { ...previous, buySettings: plan.buySettings } : previous,
      )
      utils.purchasePlan.getAll.setData(undefined, (previous) =>
        previous?.map((entry) =>
          entry.id === plan.id ? { ...entry, buySettings: plan.buySettings } : entry,
        ),
      )
      void utils.purchasePlan.warehouseClaims.invalidate()
      toast.success("Buy settings updated.")
      trackEvent("Fashion Buy Settings Updated")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useDeletePurchasePlan = () => {
  const utils = api.useUtils()

  return api.purchasePlan.delete.useMutation({
    onSuccess: async () => {
      await invalidateSupplyPlanListReads(utils)
      utils.purchasePlan.getQtyPlannedDataForPlans.invalidate()
      utils.purchasePlan.getVariantQuantitiesForPlans.invalidate()
      // Deleting a buy releases its warehouses.
      utils.purchasePlan.warehouseClaims.invalidate()
      toast.success("Supply plan deleted.")
      trackEvent("Supply Plan Deleted")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useAddVariantsToPurchasePlan = () => {
  const utils = api.useUtils()

  return api.purchasePlan.addVariants.useMutation({
    onSuccess: () => {
      utils.purchasePlan.get.invalidate()
      utils.purchasePlan.getAll.invalidate()
      toast.success("Variants added to supply plan.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useRemoveVariantsFromPurchasePlan = () => {
  const utils = api.useUtils()

  return api.purchasePlan.removeVariants.useMutation({
    onSuccess: () => {
      utils.purchasePlan.get.invalidate()
      toast.success("Variants removed from supply plan.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useSavePurchasePlanSnapshot = () => {
  const utils = api.useUtils()

  return api.purchasePlan.saveSnapshot.useMutation({
    onSuccess: () => {
      utils.purchasePlan.getSnapshot.invalidate()
      utils.purchasePlan.getSnapshotOptions.invalidate()
      toast.success("The snapshot was successfully saved.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useSyncPurchasePlanWithCollection = () => {
  const utils = api.useUtils()

  return api.purchasePlan.syncWithCollection.useMutation({
    onSuccess: (data) => {
      utils.purchasePlan.invalidate()

      let message = "Supply plan synced with collection. "
      if (data.variantsAdded > 0 || data.variantsRemoved > 0) {
        message += `${data.variantsAdded} variants added, ${data.variantsRemoved} variants removed. `
      }
      if (data.snapshotsAffected > 0) {
        if (data.snapshotsDeleted > 0) {
          message += `${data.snapshotsDeleted} affected snapshot(s) deleted.`
        } else {
          message += `${data.snapshotsAffected} snapshot(s) may be affected.`
        }
      }

      toast.success(message)
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}
